import type { AiModelWithStats } from '@owl/shared';
import { useCallback, useState } from 'react';
import type { ShellCtx } from '../components/Shell';
import { useToast } from '../components/Toast';
import { Icon } from '../components/icons';
import { ApiError, api } from '../lib/api';
import { timeAgo } from '../lib/time';
import { useLoader } from '../lib/useLoader';

function formatUsd(n: number): string {
  if (n === 0) return '$0.00';
  if (n < 0.01) return `$${n.toFixed(6)}`;
  return `$${n.toFixed(2)}`;
}

function formatInt(n: number): string {
  return new Intl.NumberFormat('es-ES').format(n);
}

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export function AiModels({ ctx }: { ctx: ShellCtx }) {
  const refreshTick = ctx.refreshTick;
  const [items, setItems] = useState<AiModelWithStats[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { displayLabel: string; color: string }>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const { push } = useToast();

  const load = useCallback(async () => {
    try {
      const { items } = await api.listAiModels();
      setItems(items);
      setDrafts({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useLoader(load, refreshTick);

  function draftFor(m: AiModelWithStats) {
    return drafts[m.id] ?? { displayLabel: m.displayLabel, color: m.color };
  }

  function setDraft(id: string, patch: Partial<{ displayLabel: string; color: string }>) {
    setDrafts((d) => {
      const current = d[id] ?? items?.find((m) => m.id === id) ?? { displayLabel: '', color: '' };
      return { ...d, [id]: { displayLabel: current.displayLabel, color: current.color, ...patch } };
    });
  }

  function isDirty(m: AiModelWithStats) {
    const d = drafts[m.id];
    if (!d) return false;
    return d.displayLabel !== m.displayLabel || d.color !== m.color;
  }

  async function save(m: AiModelWithStats) {
    const d = draftFor(m);
    if (!HEX_RE.test(d.color)) {
      push('Color must be a 6-digit hex (#RRGGBB)');
      return;
    }
    if (d.displayLabel.trim().length === 0 || d.displayLabel.length > 128) {
      push('Display label must be between 1 and 128 characters');
      return;
    }
    setSavingId(m.id);
    try {
      await api.updateAiModel(m.id, { displayLabel: d.displayLabel.trim(), color: d.color });
      push(`${m.provider}/${m.model} updated`);
      await load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Update failed');
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <div className="view-title">AI models</div>
          <div className="view-sub">
            {items?.length ?? 0} model{(items?.length ?? 0) === 1 ? '' : 's'} · auto-registered from
            ingest. Customize label and color to taste.
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {items === null ? (
        <div className="empty-state">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          No models yet. They will appear here automatically the first time an event with their
          (provider, model) hits the ingest endpoint.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Color</th>
                <th>Provider / Model</th>
                <th>Display label</th>
                <th>Events 24h</th>
                <th>Cost 24h</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const d = draftFor(m);
                const dirty = isDirty(m);
                return (
                  <tr key={m.id} className="data-row">
                    <td>
                      <input
                        type="color"
                        value={d.color}
                        onChange={(e) => setDraft(m.id, { color: e.target.value })}
                        style={{
                          width: 32,
                          height: 24,
                          border: '1px solid var(--border-1)',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                        title={d.color}
                      />
                    </td>
                    <td>
                      <span className="dim">{m.provider}</span> · <strong>{m.model}</strong>
                    </td>
                    <td>
                      <input
                        className="form-input"
                        value={d.displayLabel}
                        onChange={(e) => setDraft(m.id, { displayLabel: e.target.value })}
                        maxLength={128}
                        style={{ minWidth: 220 }}
                      />
                    </td>
                    <td className="mono">{formatInt(m.events24h)}</td>
                    <td className="mono">{formatUsd(m.cost24hUsd)}</td>
                    <td className="dim">{timeAgo(m.updatedAt)}</td>
                    <td>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="cta"
                          disabled={!dirty || savingId === m.id}
                          onClick={() => save(m)}
                          title="Save changes"
                        >
                          {savingId === m.id ? (
                            'Saving…'
                          ) : (
                            <>
                              <Icon.Check size={12} /> Save
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
